import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { PLATFORMS } from "@/lib/api";

const RECIPES = [
  {
    id: "stelle",
    title: "Risotto della Ragazza delle Stelle",
    artwork: "La Ragazza delle Stelle · olio su tela, 2022",
    image: "/images/ricetta-stelle.png",
    intro:
      "Il cielo notturno del quadro diventa piatto: il nero di seppia è il fondo stellato, la burrata è la luce del volto, l'oro commestibile sono le stelle.",
    ingredients: [
      "Riso Carnaroli e nero di seppia",
      "Crema di burrata fresca",
      "Polvere d'oro commestibile",
      "Fiori eduli (viola, borragine)",
      "Brodo di mare, olio evo, erba cipollina",
    ],
    steps: [
      "Tosta il riso e sfuma; porta a cottura con brodo di mare e nero di seppia, mantecando fino a un nero lucido e profondo.",
      "Stendi il risotto 'all'onda' e adagia al centro la crema di burrata, come la luce che abita il volto della ragazza.",
      "Completa con polvere d'oro, fiori eduli e gocce di olio all'erba cipollina: il cielo stellato è servito.",
    ],
  },
  {
    id: "madonna",
    title: "Sfera Sacra della Madonna de lu Tampone",
    artwork: "Madonna de lu Tampone · olio su tela, 2022",
    image: "/images/ricetta-madonna.png",
    intro:
      "Il sacro e il quotidiano nello stesso boccone: una sfera candida come l'iconografia mariana, un cuore scuro come la cronaca. Servita con candela accesa, come un piccolo altare.",
    ingredients: [
      "Ricotta di bufala freschissima",
      "Miele di zagara",
      "Marmellata di fichi neri (il cuore)",
      "Cialda croccante di pane carasau al sesamo",
      "Olio evo alle erbe",
    ],
    steps: [
      "Lavora la ricotta di bufala con il miele di zagara e forma una sfera perfetta con un cuore di marmellata di fichi neri.",
      "Adagia la sfera su una cialda di carasau al sesamo, lucida con altro miele e qualche goccia di olio alle erbe.",
      "Servi con una piccola candela accesa accanto al piatto: si mangia in silenzio, come si guarda un'icona.",
    ],
  },
];

export default function Ricette() {
  return (
    <div data-testid="ricette-page">
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4">Le ricette d'arte</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
            Dal quadro al piatto
          </h1>
          <p className="text-sm text-[#C9C4B8] leading-relaxed max-w-2xl">
            Ogni ricetta di Wilder nasce da un'opera di Italo Blandolino: la osservi, la cucini, la mangi.
            Qui trovi le due ricette pilota. Le versioni professionali complete, con dosi e tecniche dello
            Chef Antonio, vivono su Gronda e Patreon.
          </p>
        </div>
      </section>

      {RECIPES.map((r, idx) => (
        <section key={r.id} className={`border-b border-[#2A2A2A] ${idx % 2 ? "bg-[#141414]" : ""}`}>
          <div className={`max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`} data-testid={`recipe-${r.id}`}>
            <div className={idx % 2 ? "lg:order-2" : ""}>
              <div className="art-frame aspect-[16/11] border border-[#2A2A2A]">
                <img src={r.image} alt={r.title} loading="lazy" />
              </div>
              <p className="text-xs text-[#9a9484] mt-3 font-mono-ws">Ispirata a: {r.artwork}</p>
            </div>
            <div className={idx % 2 ? "lg:order-1" : ""}>
              <h2 className="font-display text-lg font-bold mb-4">{r.title}</h2>
              <p className="text-sm text-[#C9C4B8] italic leading-relaxed mb-7">{r.intro}</p>
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#E4BD44] mb-3">La tavolozza</h3>
              <ul className="space-y-1.5 text-sm text-[#C9C4B8] mb-7">
                {r.ingredients.map((ing) => (
                  <li key={ing} className="flex gap-2"><span className="text-[#D4AF37]">—</span>{ing}</li>
                ))}
              </ul>
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#E4BD44] mb-3">Le pennellate</h3>
              <ol className="space-y-3 text-sm text-[#C9C4B8]">
                {r.steps.map((s, i) => (
                  <li key={s} className="flex gap-3">
                    <span className="font-display font-bold text-[#E4BD44]">{i + 1}.</span>{s}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      ))}

      <section className="dark-section bg-[#121212] text-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <h2 className="font-display text-lg font-bold mb-3">Vuoi le ricette professionali complete?</h2>
          <p className="text-sm text-[#b8b2a4] mb-8 max-w-xl">
            Dosi precise, tecniche, video e tutte le nuove creazioni dello Chef Antonio: le trovi sui canali della CREW.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A] max-w-2xl">
            {PLATFORMS.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                 className="bg-[#121212] hover:bg-[#1a1a1a] transition-colors p-6 group"
                 data-testid={`ricette-platform-${p.name.toLowerCase()}`}>
                <p className="font-display text-xl font-bold text-[#D4AF37] flex items-center gap-2">
                  {p.name} <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
                <p className="text-xs text-[#8a8578] mt-1">{p.desc}</p>
              </a>
            ))}
          </div>
          <p className="text-xs text-[#8a8578] mt-8">
            Oppure <Link to="/wilder" className="ws-link text-[#D4AF37]">chiedi a Wilder</Link> una ricetta ispirata alla tua opera preferita.
          </p>
        </div>
      </section>
    </div>
  );
}
